import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {EnvironmentService} from '../environment.service';
import {ClientService} from '../auth/client.service';

type ConnectHttpOptions = {
  headers?: {[header: string]: string | string[]};
  params?: HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>};
  scopeTenant?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ConnectApiService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvironmentService);
  private readonly clientService = inject(ClientService);

  private withTenant(path: string): string {
    const clientId = (this.clientService.client?.id || '').trim();
    if (!clientId) return path;

    const alreadyScoped = /(?:^|[?&])(tenant_id|tenantId|clientId|client_id)=/i.test(path);
    if (alreadyScoped) return path;

    const delimiter = path.includes('?') ? '&' : '?';
    return `${path}${delimiter}client_id=${encodeURIComponent(clientId)}`;
  }

  private normalize(path: string, scopeTenant: boolean = true): string {
    const base = (this.env.connectBaseServerUrl || '').replace(/\/+$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${scopeTenant ? this.withTenant(cleanPath) : cleanPath}`;
  }

  get<T>(path: string, options?: ConnectHttpOptions) {
    const { scopeTenant = true, ...httpOptions } = options ?? {};
    return this.http.get<T>(this.normalize(path, scopeTenant), httpOptions);
  }

  post<T>(path: string, body?: unknown, options?: ConnectHttpOptions) {
    const { scopeTenant = true, ...httpOptions } = options ?? {};
    return this.http.post<T>(this.normalize(path, scopeTenant), body, httpOptions);
  }

  put<T>(path: string, body?: unknown, options?: ConnectHttpOptions) {
    const { scopeTenant = true, ...httpOptions } = options ?? {};
    return this.http.put<T>(this.normalize(path, scopeTenant), body, httpOptions);
  }

  delete<T>(path: string, options?: ConnectHttpOptions) {
    const { scopeTenant = true, ...httpOptions } = options ?? {};
    return this.http.delete<T>(this.normalize(path, scopeTenant), httpOptions);
  }
}
